import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { Typography } from '../Typography';

/**
 * Domain states a Badge can represent. Each state owns its label and colour tone;
 * see the Badge section of src/primer/README.md for the full state table.
 */
export const BadgeState = {
  DAYS_LEFT: 'daysLeft',
  OVERDUE: 'overdue',
  QUANTITY_LEFT: 'quantityLeft',
  DELIVERED: 'delivered',
  OUT_OF_STOCK: 'outOfStock',
  YOU_BUY: 'youBuy',
  REVIEW: 'review',
  REVIEW_SOCIAL: 'review&Social',
  SELECTED: 'selected',
  PAUSED: 'paused',
} as const;

export type BadgeState = (typeof BadgeState)[keyof typeof BadgeState];

/** Each tone must be a valid Typography colour. */
type BadgeTone = 'success' | 'warning' | 'error' | 'interactive' | 'disabled';

interface BadgeStateConfig {
  label: string;
  tone: BadgeTone;
  /** Show the `number` prop before the label. */
  numbered?: boolean;
  /** Always show this number, ignoring the `number` prop. */
  fixedNumber?: number;
  /** Label used instead of `label` when the displayed number is 1. */
  singularLabel?: string;
}

export interface BadgeProps {
  /**
   * Domain state controlling the badge label and semantic colour treatment.
   * @default BadgeState.DAYS_LEFT
   */
  state?: BadgeState;
  /**
   * Value shown before the label. Only used by the `DAYS_LEFT`, `OVERDUE`, and
   * `QUANTITY_LEFT` states; negative values are shown as 0.
   */
  number?: number;
  /** Replaces the state's default label. */
  children?: ReactNode;
  /** Optional test hook for consumers that need to identify the badge. */
  testId?: string;
}

const toneClasses: Record<BadgeTone, string> = {
  success: 'bg-success border-success',
  warning: 'bg-warning border-warning',
  error: 'bg-error border-error',
  interactive: 'bg-informative border-interactive-moderate',
  disabled: 'bg-disabled border-default',
};

const stateConfig: Record<BadgeState, BadgeStateConfig> = {
  [BadgeState.DAYS_LEFT]: {
    label: 'days left',
    singularLabel: 'day left',
    tone: 'warning',
    numbered: true,
  },
  [BadgeState.OVERDUE]: {
    label: 'days overdue',
    singularLabel: 'day overdue',
    tone: 'error',
    numbered: true,
  },
  [BadgeState.QUANTITY_LEFT]: { label: 'left', tone: 'success', numbered: true },
  [BadgeState.DELIVERED]: { label: 'We Ship', tone: 'interactive' },
  [BadgeState.OUT_OF_STOCK]: { label: 'left', tone: 'disabled', fixedNumber: 0 },
  [BadgeState.YOU_BUY]: { label: 'You buy', tone: 'interactive' },
  [BadgeState.REVIEW]: { label: 'Review', tone: 'interactive' },
  [BadgeState.REVIEW_SOCIAL]: { label: 'Review & social post', tone: 'interactive' },
  [BadgeState.SELECTED]: { label: 'Selected', tone: 'success' },
  [BadgeState.PAUSED]: { label: 'Program paused', tone: 'warning' },
};

/**
 * Primer Badge component.
 *
 * A sealed, state-driven status pill for campaign statuses and informational tags.
 * The `state` prop selects both the label and the colour tone, so consumers never
 * pass colours or class names. `className` is intentionally not exposed; layout
 * belongs on the parent container.
 *
 * Numbered states (`DAYS_LEFT`, `OVERDUE`, `QUANTITY_LEFT`) render `number` in bold
 * before the label, and use a singular label when the number is 1. `OUT_OF_STOCK`
 * always shows `0`. Pass `children` to override the label text.
 *
 * @example
 * <Badge state={BadgeState.DAYS_LEFT} number={30} />
 *
 * @example
 * // Label override
 * <Badge state={BadgeState.SELECTED}>Chosen</Badge>
 *
 * @see {@link https://www.figma.com/design/jkuMhkzDQH2lAUYa8CQvto/Primer-DS?node-id=7784-458&m=dev|Figma Design}
 * @see src/primer/README.md
 */
export function Badge({ state = BadgeState.DAYS_LEFT, number, children, testId }: BadgeProps) {
  const config = stateConfig[state];
  const displayNumber =
    config.fixedNumber ??
    (config.numbered && number !== undefined ? Math.max(number, 0) : undefined);
  const displayLabel =
    displayNumber === 1 && config.singularLabel ? config.singularLabel : config.label;

  return (
    <span
      data-testid={testId}
      className={cn(
        'inline-flex items-center justify-center rounded-medium border px-2 py-1',
        toneClasses[config.tone]
      )}
    >
      <Typography variant="body" size="small" color={config.tone} as="span">
        {displayNumber !== undefined && (
          <>
            <Typography
              variant="body"
              size="small"
              color={config.tone}
              decoration={{ bold: true }}
              as="span"
            >
              {displayNumber}
            </Typography>{' '}
          </>
        )}
        {children ?? displayLabel}
      </Typography>
    </span>
  );
}

export default Badge;
